import React, { useRef, useCallback, useMemo, MouseEventHandler } from 'react';
import { MarkerCanvasContext, MarkerCanvasProvider } from './MarkerCanvasContext';
import TimelineMarkersRenderer from './TimelineMarkersRenderer';
import { TimelineStateConsumer } from '../timeline/TimelineStateContext';

const staticStyles: React.CSSProperties = {
  position: 'absolute',
  left: 0,
  right: 0,
  top: 0,
  bottom: 0,
};

interface MarkerCanvasProps {
  getDateFromLeftOffsetPosition: (offset: number) => number;
  children?: React.ReactNode;
}

function MarkerCanvasInner({ getDateFromLeftOffsetPosition, children }: MarkerCanvasProps) {
  const containerEl = useRef<HTMLDivElement>(null);
  const subscriptionRef = useRef<
    ((s: { leftOffset: number; date: number; isCursorOverCanvas: boolean }) => void) | null
  >(null);

  const handleMouseMove: MouseEventHandler<HTMLDivElement> = useCallback(
    (evt) => {
      if (subscriptionRef.current !== null && containerEl.current) {
        const { pageX } = evt;
        const { left: containerLeft = 0 } = containerEl.current.getBoundingClientRect();
        const canvasX = pageX - containerLeft;
        const date = getDateFromLeftOffsetPosition(canvasX);
        subscriptionRef.current({
          leftOffset: canvasX,
          date,
          isCursorOverCanvas: true,
        });
      }
    },
    [getDateFromLeftOffsetPosition]
  );

  const handleMouseLeave: MouseEventHandler<HTMLDivElement> = useCallback(() => {
    if (subscriptionRef.current !== null) {
      subscriptionRef.current({ leftOffset: 0, date: 0, isCursorOverCanvas: false });
    }
  }, []);

  const handleMouseMoveSubscribe: MarkerCanvasContext['subscribeToMouseOver'] = useCallback(
    (sub) => {
      subscriptionRef.current = sub;
      return () => {
        subscriptionRef.current = null;
      };
    },
    []
  );

  const state: MarkerCanvasContext = React.useMemo(
    () => ({
      subscribeToMouseOver: handleMouseMoveSubscribe,
    }),
    [handleMouseMoveSubscribe]
  );

  return (
    <MarkerCanvasProvider value={state}>
      <div
        style={staticStyles}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        ref={containerEl}
      >
        <TimelineMarkersRenderer />
        {children}
      </div>
    </MarkerCanvasProvider>
  );
}

function MarkerCanvasWrapper(props: Omit<MarkerCanvasProps, 'getDateFromLeftOffsetPosition'>) {
  return (
    <TimelineStateConsumer>
      {({ getDateFromLeftOffsetPosition }) => (
        <MarkerCanvasInner
          getDateFromLeftOffsetPosition={getDateFromLeftOffsetPosition}
          {...props}
        />
      )}
    </TimelineStateConsumer>
  );
}

MarkerCanvasWrapper.displayName = 'MarkerCanvas';

export default MarkerCanvasWrapper;
